import type { AnalysisType } from 'config/analysisConfig';
import {
  buildMockCenterlinePath,
  type MockCenterlinePoint,
} from './cprImageResources';

type MockImageContext = {
  analysisType?: AnalysisType;
  branchGraphDataset?: {
    getLumenDiameterValue?: (branchId: number, index: number) => number | null;
    getWallDiameterValue?: (branchId: number, index: number) => number | null;
  };
  centerlinePoints?: Record<string | number, MockCenterlinePoint[]>;
  config?: {
    coronary_front_expand?: number;
    coronary_back_expand?: number;
    colors?: Record<string, string>;
  };
};

type MockImageSvgInput = {
  path: string;
  width: number;
  height: number;
};

const contexts = new Map<string, MockImageContext>();

function toSafeDimension(value: number) {
  return Math.max(1, Math.floor(Number.isFinite(value) ? value : 1));
}

function escapeAttr(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function stripQuery(path: string) {
  return path.split('?')[0];
}

function parseMockPath(path: string) {
  const clean = stripQuery(path);
  const parts = clean.split('/');
  if (clean.startsWith('mock://cpr/')) {
    return {
      kind: 'cpr' as const,
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: parts[5],
      angleDeg: Number(parts[6]),
      index: null,
      layer: parts[7] ?? 'base',
    };
  }
  if (clean.startsWith('mock://cross-section/')) {
    return {
      kind: 'cross-section' as const,
      seriesId: parts[3],
      branchId: Number(parts[4]),
      mode: null,
      angleDeg: 0,
      index: Number(parts[5]),
      layer: parts[6] ?? 'base',
    };
  }
  return null;
}

function colorForLayer(layer: string, context?: MockImageContext) {
  const colors = context?.config?.colors ?? {};
  const defaults: Record<string, string> = {
    lumen: 'rgba(46, 204, 113, 0.28)',
    lumenoutline: colors.lumenoutline ?? 'rgba(99, 230, 190, 0.85)',
    plaque: 'rgba(255, 206, 84, 0.26)',
    plaqueoutline: colors.walloutline ?? 'rgba(255, 171, 64, 0.85)',
    fai: 'rgba(77, 171, 247, 0.24)',
  };
  return defaults[layer] ?? 'rgba(255,255,255,0.3)';
}

function numericOr(value: number | null | undefined, fallback: number) {
  return Number.isFinite(value) && Number(value) > 0 ? Number(value) : fallback;
}

function getDiameterAt(
  context: MockImageContext | undefined,
  branchId: number,
  index: number,
  target: 'lumen' | 'wall',
) {
  const graph = context?.branchGraphDataset;
  if (target === 'wall') {
    return numericOr(graph?.getWallDiameterValue?.(branchId, index), 6);
  }
  return numericOr(graph?.getLumenDiameterValue?.(branchId, index), 3);
}

function makeBaseSvg(width: number, height: number, label: string) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-source="mock-grayscale">
<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="rgb(36,36,36)"/><stop offset="1" stop-color="rgb(138,138,138)"/></linearGradient></defs>
<rect width="100%" height="100%" fill="url(#g)"/>
<text x="${Math.max(4, width * 0.05)}" y="${Math.max(12, height * 0.08)}" fill="rgba(255,255,255,0.22)" font-size="10">${escapeAttr(label)}</text>
</svg>`;
}

function buildCprLayerSvg({
  width,
  height,
  branchId,
  layer,
  mode,
  angleDeg,
  context,
}: {
  width: number;
  height: number;
  branchId: number;
  layer: string;
  mode: string | null;
  angleDeg: number;
  context?: MockImageContext;
}) {
  if (layer === 'base') {
    return makeBaseSvg(width, height, `${mode ?? 'cpr'} ${branchId}`);
  }

  const isOutline = layer.endsWith('outline');
  const target = layer.startsWith('plaque') ? 'wall' : 'lumen';
  const front = Math.max(
    0,
    Math.floor(context?.config?.coronary_front_expand ?? 0),
  );
  const back = Math.max(
    0,
    Math.floor(context?.config?.coronary_back_expand ?? 0),
  );
  const validEnd = Math.max(front, height - 1 - back);
  const sampleCount = Math.max(8, Math.min(80, Math.floor(height / 8)));
  const angleRad = (Number.isFinite(angleDeg) ? angleDeg : 0) * Math.PI / 180;
  const centerlinePath = buildMockCenterlinePath({
    indexStart: 0,
    indexEnd: Math.max(0, height - 1),
    mode: mode === 'stretched' ? 'stretched' : 'straightened',
    width,
    height,
    angleDeg,
    centerlinePoints: context?.centerlinePoints?.[branchId],
  });
  const left: Array<[number, number]> = [];
  const right: Array<[number, number]> = [];
  const diameterScale = target === 'wall' ? 3.4 : 3.0;

  for (let i = 0; i < sampleCount; i += 1) {
    const t = sampleCount === 1 ? 0 : i / (sampleCount - 1);
    const y = t * Math.max(0, height - 1);
    const index = Math.round(t * Math.max(1, height - 1));
    if (index < front || index > validEnd) continue;
    const centerPoint =
      centerlinePath[
        Math.min(centerlinePath.length - 1, Math.max(0, index))
      ];
    const fallbackCenterX =
      mode === 'stretched'
        ? width * 0.5 +
          Math.sin(angleRad + t * Math.PI * 2) * Math.min(width * 0.16, 48)
        : width * 0.5;
    const centerX = Number.isFinite(centerPoint?.x)
      ? centerPoint.x
      : fallbackCenterX;
    const diameter = getDiameterAt(context, branchId, index, target);
    const half = Math.max(2, Math.min(width * 0.45, diameter * diameterScale));
    left.push([centerX - half, y]);
    right.push([centerX + half, y]);
  }

  if (left.length < 2 || right.length < 2) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-source="branchGraphDataset"></svg>`;
  }

  const polygon = [...left, ...right.reverse()]
    .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ');
  const color = colorForLayer(layer, context);
  const fill = isOutline ? 'none' : color;
  const stroke = isOutline ? color : 'rgba(255,255,255,0.12)';
  const strokeWidth = isOutline ? 1.5 : 0.8;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-source="branchGraphDataset">
<polygon points="${polygon}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
</svg>`;
}

function buildCrossSectionLayerSvg({
  width,
  height,
  branchId,
  index,
  layer,
  context,
}: {
  width: number;
  height: number;
  branchId: number;
  index: number | null;
  layer: string;
  context?: MockImageContext;
}) {
  if (layer === 'base') {
    return makeBaseSvg(width, height, `CS ${branchId}:${index ?? '-'}`);
  }

  const safeIndex = Number.isFinite(index) ? Number(index) : 0;
  const target = layer.startsWith('plaque') ? 'wall' : 'lumen';
  const isOutline = layer.endsWith('outline');
  const diameter = getDiameterAt(context, branchId, safeIndex, target);
  const radius = Math.max(8, Math.min(width, height) * 0.08 + diameter * 4);
  const plaqueRadius = Math.max(radius + 5, radius * 1.45);
  const r = target === 'wall' ? plaqueRadius : radius;
  const color = colorForLayer(layer, context);
  const fill = isOutline ? 'none' : color;
  const stroke = isOutline ? color : 'rgba(255,255,255,0.12)';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" data-source="branchGraphDataset">
<circle cx="${width * 0.5}" cy="${height * 0.5}" r="${r.toFixed(2)}" fill="${fill}" stroke="${stroke}" stroke-width="${isOutline ? 2 : 1}"/>
</svg>`;
}

export function registerMockImageDataContext(
  seriesId: string | undefined,
  context: MockImageContext,
) {
  if (!seriesId) return;
  contexts.set(seriesId, context);
}

export function unregisterMockImageDataContext(seriesId: string | undefined) {
  if (!seriesId) return;
  contexts.delete(seriesId);
}

export function createMockImageSvg({ path, width, height }: MockImageSvgInput) {
  const safeWidth = toSafeDimension(width);
  const safeHeight = toSafeDimension(height);
  const parsed = parseMockPath(path);
  if (!parsed) {
    return makeBaseSvg(safeWidth, safeHeight, 'mock');
  }

  const context = contexts.get(parsed.seriesId);
  if (parsed.kind === 'cpr') {
    return buildCprLayerSvg({
      width: safeWidth,
      height: safeHeight,
      branchId: parsed.branchId,
      layer: parsed.layer,
      mode: parsed.mode,
      angleDeg: parsed.angleDeg,
      context,
    });
  }

  return buildCrossSectionLayerSvg({
    width: safeWidth,
    height: safeHeight,
    branchId: parsed.branchId,
    index: parsed.index,
    layer: parsed.layer,
    context,
  });
}
